import type { ProjectTask } from '@/types/odoo';

interface FaseOpgelijstToggleProps {
  taskId: number;
  isEasykit: boolean;
  childTasks: ProjectTask[];
  canToggle: boolean;
  getOpgelijstValue: (id: number) => boolean;
  onToggle: (id: number, val: boolean) => Promise<void>;
  /**
   * 'header' renders the inline switch (EasyKit) or count badge (non-EasyKit),
   * shown in the fase header row. 'children' renders the per-child checkbox
   * list, shown in the expanded fase body. Splitting by variant preserves the
   * two distinct DOM positions of the original FaseSection.
   */
  variant: 'header' | 'children';
}

/**
 * Opgelijst controls for one fase. Dumb: state + persistence are delegated via
 * getOpgelijstValue / onToggle. Behaviour matches the original FaseSection:
 * - EasyKit + header: a single switch on the fase task itself.
 * - non-EasyKit + header: a "{done}/{total} opgelijst" summary badge.
 * - non-EasyKit + children: per-child-task toggle rows.
 */
export function FaseOpgelijstToggle({
  taskId,
  isEasykit,
  childTasks,
  canToggle,
  getOpgelijstValue,
  onToggle,
  variant,
}: FaseOpgelijstToggleProps) {
  if (!canToggle) return null;

  if (variant === 'header') {
    if (isEasykit && taskId !== 0) {
      const opgelijstValue = getOpgelijstValue(taskId);
      return (
        <div
          role="switch"
          aria-checked={opgelijstValue}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(taskId, !opgelijstValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              onToggle(taskId, !opgelijstValue);
            }
          }}
          className={`ml-3 flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full transition cursor-pointer ${
            opgelijstValue
              ? 'bg-td-green/10 text-td-green'
              : 'bg-orange-50 text-orange-500'
          }`}
        >
          <div className={`relative w-7 h-3.5 rounded-full transition-colors ${opgelijstValue ? 'bg-td-green' : 'bg-orange-300'}`}>
            <div className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white shadow transition-transform ${opgelijstValue ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
          </div>
          <span className="w-[75px] text-center">{opgelijstValue ? 'Opgelijst' : 'Niet opgelijst'}</span>
        </div>
      );
    }

    if (!isEasykit && childTasks.length > 0) {
      const doneCount = childTasks.filter((t) => getOpgelijstValue(t.id)).length;
      const total = childTasks.length;
      const badgeClass =
        doneCount === total
          ? 'bg-green-100 text-green-700'
          : doneCount > 0
            ? 'bg-amber-100 text-amber-700'
            : 'bg-gray-100 text-gray-500';
      return (
        <span className={`ml-3 shrink-0 text-xs font-semibold px-2.5 py-0.5 rounded-full ${badgeClass}`}>
          {doneCount}/{total} opgelijst
        </span>
      );
    }

    return null;
  }

  // variant === 'children'
  if (!isEasykit && childTasks.length > 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
        {childTasks.map((task) => {
          const checked = getOpgelijstValue(task.id);
          return (
            <div key={task.id} className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2 truncate mr-3">
                <span className="text-sm text-td-navy truncate">{task.name}</span>
                <span className="shrink-0 text-xs text-gray-400">
                  {task.date_deadline
                    ? task.date_deadline.slice(8, 10) + '-' + task.date_deadline.slice(5, 7) + '-' + task.date_deadline.slice(0, 4)
                    : '–'}
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={`${task.name} opgelijst`}
                onClick={() => onToggle(task.id, !checked)}
                className={`shrink-0 relative w-8 h-[18px] rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-td-blue/40 ${checked ? 'bg-td-green' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-[3px] w-3 h-3 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[14px]' : 'translate-x-[3px]'}`} />
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
}
